import Bugsnag, {
	type Client,
	type Event,
} from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';
import type { App } from 'vue';
import { getErrorMetadata } from '../utils/annotate-error';
import isChromeIosInlineScriptNoise from '../utils/is-chrome-ios-inline-script-noise';
import isServiceWorkerRegistrationNoise from '../utils/is-service-worker-registration-noise';
import sanitizeBreadcrumbUrls from '../utils/sanitize-breadcrumb-urls';
import sanitizeUrl, { type SanitizeUrlOptions } from '../utils/sanitize-url';
import getBugsnagBreadcrumbTypes from './bugsnag-breadcrumbs';
import { BUGSNAG_REDACTED_KEYS } from './bugsnag-privacy';

interface BugsnagReleaseConfig {
	apiKey: string;
	appVersion: string;
	enabledReleaseStages: string[];
	releaseStage: string;
}

interface ConfigureBugsnagOptions {
	appType?: string;
	appVersion: string;
	getUrlOptions: () => SanitizeUrlOptions;
	includeUserBreadcrumbs?: boolean;
	onError?: (event: Event) => void;
	releaseStage: string;
}

const BUGSNAG_API_KEY = '7108d26410396261866519684a732fd5';
export const BUGSNAG_ENABLED_RELEASE_STAGES: string[] = [
	'local',
	'staging',
	'live',
];

/**
 * Starts browser and Vue error capture with the shared telemetry privacy policy.
 * App-owned metadata is resolved when each event is reported.
 *
 * @param appInstance Vue application to instrument before mounting.
 * @param options Release identity, URL policy and optional app-owned metadata.
 * @returns Started Bugsnag client for explicit error reporting.
 */
export function configureBugsnag(
	appInstance: App,
	options: ConfigureBugsnagOptions,
): Client {
	const client = Bugsnag.start({
		...getBugsnagReleaseConfig(
			options.appVersion,
			options.releaseStage,
		),
		appType: options.appType,
		collectUserIp: false,
		enabledBreadcrumbTypes: getBugsnagBreadcrumbTypes().filter(
			(type) => options.includeUserBreadcrumbs !== false || type !== 'user',
		),
		onBreadcrumb: (breadcrumb) => {
			if (options.includeUserBreadcrumbs === false && breadcrumb.type === 'user') {
				return false;
			}

			return sanitizeBreadcrumbUrls(
				breadcrumb,
				options.getUrlOptions(),
			);
		},
		onError: (report) => {
			if (
				isServiceWorkerRegistrationNoise(report)
				|| isChromeIosInlineScriptNoise(report)
			) {
				return false;
			}

			if (report.request.url) {
				report.request.url = sanitizeUrl(
					report.request.url,
					options.getUrlOptions(),
				);
			}

			options.onError?.(report);
			const attached = getErrorMetadata(report.originalError);

			if (attached) {
				Object.keys(attached).forEach((section) => {
					report.addMetadata(
						section,
						attached[section],
					);
				});
			}
		},
		plugins: [new BugsnagPluginVue()],
		redactedKeys: BUGSNAG_REDACTED_KEYS,
	});
	const bugsnagVue = Bugsnag.getPlugin('vue');

	if (bugsnagVue) appInstance.use(bugsnagVue);

	return client;
}

/**
 * Supplies the same project and release policy to error and performance capture.
 *
 * @param appVersion Build identifier of the application being started.
 * @param releaseStage Deployment stage of the current application.
 * @returns Shared Bugsnag project and release metadata.
 */
export function getBugsnagReleaseConfig(
	appVersion: string,
	releaseStage: string,
): BugsnagReleaseConfig {
	return {
		apiKey: BUGSNAG_API_KEY,
		appVersion,
		enabledReleaseStages: BUGSNAG_ENABLED_RELEASE_STAGES,
		releaseStage,
	};
}
